import { commitStaticAttributeValue, createAttributePart } from './render-dom-attribute-parts.js';
import { commitStaticNodeText, createNodePart } from './render-dom-parts.js';
import { isStaticBinding } from './render-dom-static.js';
import { getBinding } from './render-template.js';
import { isAttributePartType, runtimeError } from './render-values.js';
import { runWithOwner } from './signal.js';
/**
 * Bindings are compared by value identity (`Object.is`); a composite
 * attribute's binding resolves to a fresh array per read, so composites
 * compare element-wise. Mutating a bound value in place without changing its
 * identity is outside the update contract — change flows through new binding
 * values or through signals.
 */
export function bindingUnchanged(previous, next) {
    if (Object.is(previous, next))
        return true;
    if (Array.isArray(previous) && Array.isArray(next) && previous.length === next.length) {
        for (let index = 0; index < previous.length; index += 1) {
            if (!Object.is(previous[index], next[index]))
                return false;
        }
        return true;
    }
    return false;
}
/**
 * Commits every static slot of the walking family that sorts before
 * `beforeIndex`, advancing and returning the slot cursor.
 *
 * `staticIndices` is family-partitioned exactly like `parts`, so one cursor
 * spans both passes: a slot belonging to the other family ends the drain,
 * and the next pass resumes where this one stopped.
 */
export function commitStaticSlotsBefore(host, cursor, beforeIndex, nodePass) {
    const statics = host.staticIndices;
    if (!statics)
        return cursor;
    const specs = host.template.partSpecs;
    while (cursor < statics.length) {
        const ordinal = statics[cursor];
        const spec = ordinal === undefined ? undefined : specs[ordinal];
        if (ordinal === undefined || !spec)
            break;
        if (isAttributePartType(spec.type) === nodePass)
            break;
        if (spec.index >= beforeIndex)
            break;
        commitStaticSlot(host, spec, cursor, ordinal);
        cursor += 1;
    }
    return cursor;
}
/**
 * Commits one static slot's current binding straight to the DOM — no part
 * object exists to route through (#1398). The commit mirrors the live path
 * of that slot's kind exactly; only the retained state is gone.
 *
 * `ordinal` is the slot's position in the spec list — deliberately distinct
 * from `spec.index`, the hole number: a composite attribute
 * (`viewBox="0 0 ${w} ${h}"`) is ONE spec carrying two holes, so every later
 * spec's ordinal sits below its hole index. Plan entries are indexed by
 * ordinal, and `staticIndices` stores ordinals precisely so this lookup
 * stays aligned.
 */
function commitStaticSlot(host, spec, cursor, ordinal) {
    const ref = host.staticRefs?.[cursor];
    const value = getBinding(host.bindings, spec);
    if (isAttributePartType(spec.type)) {
        if (ref?.nodeType !== 1) {
            throw runtimeError('RT401', `Static slot ${spec.index} lost the element it commits through`);
        }
        const entry = host.staticPlan?.entries[ordinal];
        commitStaticAttributeValue(spec, ref, entry?.value || spec.marker, value);
        return;
    }
    if (ref?.nodeType !== 1) {
        throw runtimeError('RT401', `Static slot ${spec.index} lost the range it commits into`);
    }
    commitStaticNodeText(host.container?.ownerDocument || globalThis.document, ref, value);
}
/**
 * Update-pass counterpart of `commitStaticSlotsBefore`: every slot of the
 * walking family that sorts before `beforeIndex` either re-commits its (still
 * static) new value or is promoted to a live part (#678), and an unchanged
 * slot is skipped without a DOM write.
 *
 * A promotion SPLICES its slot out of `staticIndices`/`staticRefs`, so the
 * cursor deliberately does not advance past it: the following slot has moved
 * into its place.
 */
export function updateStaticSlotsBefore(host, cursor, beforeIndex, previous, nodePass) {
    const statics = host.staticIndices;
    if (!statics)
        return cursor;
    const specs = host.template.partSpecs;
    while (cursor < statics.length) {
        const ordinal = statics[cursor];
        const spec = ordinal === undefined ? undefined : specs[ordinal];
        if (ordinal === undefined || !spec)
            break;
        if (isAttributePartType(spec.type) === nodePass)
            break;
        if (spec.index >= beforeIndex)
            break;
        const next = getBinding(host.bindings, spec);
        if (bindingUnchanged(getBinding(previous, spec), next)) {
            cursor += 1;
        }
        else if (isStaticBinding(spec, next)) {
            commitStaticSlot(host, spec, cursor, ordinal);
            cursor += 1;
        }
        else {
            promoteStaticSlot(host, spec, cursor, ordinal);
        }
    }
    return cursor;
}
/**
 * Builds the live part a static slot becomes the moment a signal arrives
 * where a constant used to be (#678), and retires the slot.
 *
 * Everything the slot never retained is re-derived here: the element from
 * `staticRefs`, the marker text from the shared plan. A node slot is always
 * an elided hole (#1354), so its part binds straight to the range parent.
 */
function promoteStaticSlot(host, spec, cursor, ordinal) {
    const ref = host.staticRefs?.[cursor];
    const entry = host.staticPlan?.entries[ordinal];
    // Build first, so a slot the rebuild refuses (RT401) stays a slot; then
    // retire it: the update walk keeps its cursor in place after a promotion
    // and relies on the next slot having moved into this position (#1427).
    const part = rebuildSlotPart(host, spec, ref, entry);
    host.staticIndices?.splice(cursor, 1);
    host.staticRefs?.splice(cursor, 1);
    if (host.staticIndices !== null && host.staticIndices.length === 0) {
        host.staticIndices = null;
        host.staticRefs = null;
        host.staticPlan = null;
    }
    (host.promotedParts ??= []).push(part);
    part.disposer = runWithOwner(host.ensureOwner(), () => host.createPartEffect(part));
}
/** The live part a promoted static slot binds through. */
function rebuildSlotPart(host, spec, ref, entry) {
    const element = ref && ref.nodeType === 1 ? ref : undefined;
    if (isAttributePartType(spec.type)) {
        return createAttributePart(spec, element, entry?.name ?? spec.marker, 
        // The authored prefix and suffix travel with the part: a signal that
        // arrives at mount and one that arrives on a later update must commit
        // the same `title="Row ${x}"` text (#1427).
        entry?.value || spec.marker);
    }
    // A node slot is always an elided hole (#1354): its part takes the range
    // parent, exactly as the bind path would have built it.
    if (!element) {
        throw runtimeError('RT401', `Static slot ${spec.index} lost the range it commits into`);
    }
    return createNodePart(spec, undefined, undefined, element, host.container?.ownerDocument || globalThis.document, host.domAdapter);
}
//# sourceMappingURL=render-dom-static-slots.js.map